import _ from "lodash";
import moment from "moment";
import React from "react";
import CalendarAddExerciseModal from "../../../parts/modals/calendar-add-exercise";
import CalendarDuplicateWorkoutModal from "../../../parts/modals/calendar-duplicate-workout";
import CalendarMoveWorkoutModal from "../../../parts/modals/calendar-move-workout";
import JWPlayerVideoModal from "../../../parts/modals/jwplayer-video";
import WeekDay from "../../athlete-portal/calendar/components/WeekDay";

const {
  DATE_FORMATS: { DATE_DAY_FORMAT, UTC },
} = require("../../../constants");

import { RillContext } from "../../../rill/RillReact/share/base";
import { formattedTarget } from "../../../utils/actuals";
import {
  buildVideoModalUrl,
  findVideoNeighbors,
} from "../../../utils/video-modal-url";
import CalendarWeekComponent from "./CalendarWeekComponent";

class View extends React.Component {
  render() {
    const {
      activeWeek,
      endOfWeek,
      query,
      activeUser,
      workoutNotes,
      workoutCoachNotes,
      dailyStats,
      fromMetric,
      organizationOwner,
      clientConfiguration,
    } = this.context.locals;

    const athleteId = _.get(this.context.locals, "athlete._id") || activeUser._id;

    return (
      <CalendarWeekComponent
        activeWeek={activeWeek}
        endOfWeek={endOfWeek}
        query={query}
        activeUser={activeUser}
        activeUserToRequest={this.context.locals.activeUserToRequest}
        activeExercises={this.context.locals.activeExercises}
        athleteId={athleteId}
        fromMetric={fromMetric}
        organizationOwner={organizationOwner}
        clientConfiguration={clientConfiguration}
        workoutNotes={workoutNotes}
        workoutCoachNotes={workoutCoachNotes}
        WeekDay={WeekDay}
        dailyStats={dailyStats}
      />
    );
  }
}

View.contextType = RillContext;

const CalendarWeek = (app) => {
  app.get("/", async ({ req, res, locals, load }, next) => {
    if (req.query.view !== "week") return next();

    const { query } = req;
    _.defaults(query, { week: moment().format(DATE_DAY_FORMAT) });
    const modal = _.get(query, "modal", {});

    const weekDay = locals.activeUser.startingCalendarDay || 0;
    locals.activeWeek = moment(query.week, DATE_DAY_FORMAT)
      .startOf("week")
      .weekday(weekDay);
    locals.endOfWeek = locals.activeWeek.clone().add(6, "days");

    const start = query.start
      ? moment(query.start, DATE_DAY_FORMAT)
      : locals.activeWeek;
    const end = query.end
      ? moment(query.end, DATE_DAY_FORMAT)
      : locals.endOfWeek;

    const isStartAfterToday = start.isAfter(moment(), "day");
    const endForPersonalRecords = end.isAfter(moment(), "day")
      ? moment().startOf("day").format(UTC)
      : end.clone().endOf("day").format(UTC);

    await Promise.all([
      load("activeExercises", {
        start,
        end,
      }).then(async (exercises) => {
        const cycles = _.uniqBy(
          exercises.filter((e) => e.cycle),
          "cycle.id"
        ).map((exercise) => exercise.cycle.id);
        await load("cycles", cycles);
      }),
      ...(!isStartAfterToday
        ? [
            load("dayPersonalRecords", {
              start: start.clone().startOf("day").format(UTC),
              end: endForPersonalRecords,
              isCheckIn: true,
            }),
          ]
        : []),
    ]);

    const activeUser = locals.activeUser;

    await load(
      "workoutNotes",
      activeUser._id,
      start.format("YYYY-MM-DD"),
      end.format("YYYY-MM-DD")
    );

    if (activeUser.coach) {
      await load(
        "workoutCoachNotes",
        activeUser._id,
        activeUser.coach._id,
        start.format("YYYY-MM-DD"),
        end.format("YYYY-MM-DD")
      );
    }

    await load("dailyStats", {});

    locals.scheduledSessions = _.groupBy(locals.activeExercises, (e) =>
      moment.utc(e.utcDate?.toString() || e.date).format(DATE_DAY_FORMAT)
    );

    if (query.threadId) {
      await load("comments", {
        threadId: query.threadId,
      });
    } else {
      delete locals.comments;
    }

    let prevHref;
    let nextHref;
    if (modal.type === "exercise-set-video" && query.utcDate) {
      const dayKey = moment.utc(String(query.utcDate)).format(DATE_DAY_FORMAT);
      const dayExercises = locals.scheduledSessions?.[dayKey] || [];
      const sequence = [];
      for (const exercise of dayExercises) {
        const exerciseId = String(exercise._id);
        const primary = exercise.primary ?? "";
        const secondary = exercise.secondary ?? "";
        const type = exercise.type ?? "";
        const movement = exercise.movement ?? "";
        (exercise.sets || []).forEach((set, setIdx) => {
          const videos = set.videos || {};
          Object.keys(videos)
            .map(Number)
            .sort((a, b) => a - b)
            .forEach((vidIdx) => {
              const video = videos[vidIdx];
              if (!video?.videoId && !video?.videoOriginal) return;
              const seqOutcomeObj = set.outcomes?.[vidIdx]?.outcome || {};
              const seqIsPowerlifting =
                exercise.type === "primary" || exercise.type === "secondary";
              const { units, intensityMeasure } = locals.activeUser;
              const seqPrecision = units === "metric" ? 2 : 1;
              const seqUserMetrics = units === "imperial" ? "lb" : "kg";
              const seqRawLoad = seqOutcomeObj.load;
              const seqLoad = seqRawLoad
                ? `${_.round(locals.fromMetric(seqRawLoad, units), seqPrecision)} ${seqUserMetrics}`
                : 0;
              const seqTarget =
                seqRawLoad || seqOutcomeObj.reps || seqOutcomeObj.rpe
                  ? formattedTarget(
                      seqOutcomeObj,
                      seqIsPowerlifting,
                      set.percentRepMax || undefined,
                      units,
                      seqLoad,
                      intensityMeasure,
                      intensityMeasure !== "rpe",
                      true
                    ) || undefined
                  : undefined;
              const seqSetNumber = setIdx + 1;
              sequence.push({
                key: `${exerciseId}:${setIdx}:${vidIdx}`,
                href: buildVideoModalUrl({
                  modalType: "exercise-set-video",
                  video,
                  exerciseToEdit: exerciseId,
                  setIndex: setIdx,
                  videoIndex: vidIdx,
                  utcDate: exercise.utcDate,
                  primary,
                  secondary,
                  type,
                  movement,
                  setNumber: seqSetNumber,
                  setTarget: seqTarget,
                  setComment: set.comments?.[vidIdx] || undefined,
                }),
              });
            });
        });
      }
      const currentKey = `${query.exerciseToEdit}:${Number(query.setIndex)}:${Number(query.videoIndex)}`;
      ({ prevHref, nextHref } = findVideoNeighbors({ sequence, currentKey }));
    }

    locals.modal = {
      "exercise-set-video": (
        <JWPlayerVideoModal
          athleteId={query.uploaderId || query.athlete}
          exerciseToEdit={query.exerciseToEdit}
          locals={locals}
          mediaId={query.mediaId}
          movement={query.movement}
          originalId={query.originalId}
          primary={query.primary}
          reviewedByTheCoach={query.reviewedByTheCoach}
          secondary={query.secondary}
          setIndex={query.setIndex}
          threadId={query.threadId}
          type={query.type}
          utcDate={query.utcDate}
          videoIndex={query.videoIndex}
          prevHref={prevHref}
          nextHref={nextHref}
          setNumber={query.setNumber}
          setTarget={query.setTarget}
          setComment={query.setComment}
        />
      ),
      "calendar-add-exercise": <CalendarAddExerciseModal />,
      "calendar-duplicate-workout": <CalendarDuplicateWorkoutModal />,
      "calendar-move-workout": <CalendarMoveWorkoutModal />,
    }[modal.type];

    res.body = <View />;
  });
};

export default CalendarWeek;
